import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GlobalInput } from "./GlobalInput";
import Search from "@mui/icons-material/Search";
import Restore from "@mui/icons-material/Restore";
import Refresh from "@mui/icons-material/Refresh";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import type {
  AiReview,
  AiReviewScope,
  ContentExtraction,
  EbookPage,
  EbookPageSearchItem,
  EbookPageText,
  MediaOverlay,
  MediaOverlayInput,
  MediaOverlayKind,
} from "@ebook/contracts";
import { maxEbookPages, MediaOverlayInputSchema } from "@ebook/contracts";
import { api, ApiClientError } from "../lib/api-client";
import { uploadObjectAsset } from "../lib/asset-transfer-client";
import { EmptyState } from "./PageState";
import { PageEffectsDialog, mediaKindLabels } from "./PageEffectsDialog";
import { useTranslate } from "../i18n";

type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

type Translate = ReturnType<typeof useTranslate>;

function defaultMediaOverlay(
  kind: "youtube" | "audio_url" | "page_link",
  pageNumber: number,
  pageCount: number,
): MediaOverlay {
  const isLink = kind === "page_link";
  const id = `draft-${crypto.randomUUID()}`;
  return {
    id,
    pageNumber,
    kind,
    sourceUrl: kind === "youtube" ? "https://www.youtube.com/watch?v=" : isLink ? null : "",
    assetKey: null,
    assetUrl: null,
    contentType: null,
    sizeBytes: null,
    targetPageNumber: isLink ? Math.min(pageCount, pageNumber + 1) : null,
    rect: { left: 10, top: 10, width: isLink ? 35 : kind === "youtube" ? 50 : 45, height: isLink || kind === "audio_url" ? 8 : 30 },
    zIndex: 10,
    animationType: "none",
    animationDelayMs: 0,
    animationDurationMs: 300,
    enabled: true,
  };
}

function extractionStageLabel(extraction: ContentExtraction, __: Translate): string {
  switch (extraction.stage) {
    case "downloading":
      return __("กำลังดาวน์โหลดไฟล์ PDF");
    case "extracting_text":
      return __("กำลังอ่าน Text Layer จาก PDF");
    case "ocr":
      return __("กำลัง OCR หน้า {{processed}} จาก {{total}}", {
        processed: extraction.ocrPagesProcessed,
        total: extraction.ocrPagesTotal,
      });
    case "extracting_outline":
      return __("กำลังสร้างสารบัญ");
    case "saving":
      return __("กำลังบันทึกข้อความและสารบัญ");
    case "complete":
      return __("ดึงข้อมูลจาก PDF สำเร็จ");
    default:
      return __("กำลังรอระบบประมวลผล");
  }
}

export function EbookContentPanel({ ebookId }: { ebookId: string }) {
  const [pages, setPages] = useState<EbookPage[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [pageText, setPageText] = useState<EbookPageText | null>(null);
  const [draft, setDraft] = useState("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<EbookPageSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [extraction, setExtraction] = useState<ContentExtraction | null>(null);
  const [queueingExtraction, setQueueingExtraction] = useState(false);
  const [aiReview, setAiReview] = useState<AiReview | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDetailsOpen, setReviewDetailsOpen] = useState(false);
  const [reviewScope, setReviewScope] = useState<AiReviewScope>("selected_pages");
  const [reviewPageNumbers, setReviewPageNumbers] = useState("");
  const [queueingReview, setQueueingReview] = useState(false);
  const [mediaItems, setMediaItems] = useState<MediaOverlay[]>([]);
  const [mediaRevision, setMediaRevision] = useState(1);
  const [mediaStatus, setMediaStatus] = useState<SaveStatus>("idle");
  const [mediaError, setMediaError] = useState("");
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  const [mediaUploading, setMediaUploading] = useState<"audio_asset" | "video_asset" | null>(null);
  const [effectsDialogOpen, setEffectsDialogOpen] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);
  const mediaTimer = useRef<number | null>(null);
  const draftRef = useRef("");
  const pageTextRef = useRef<EbookPageText | null>(null);
  const saving = useRef<Promise<boolean> | null>(null);
  const mediaSaving = useRef<Promise<boolean> | null>(null);
  const mediaEditVersion = useRef(0);
  const mediaLastSavedVersion = useRef(0);
  const loadingPagesRef = useRef(false);
  const nextPageRef = useRef(1);
  const loadedCountRef = useRef(0);
  const totalRef = useRef<number | null>(null);
  const reloadedAiReviewJobRef = useRef<string | null>(null);
  const __ = useTranslate();

  const loadMediaOverlays = useCallback(async (pageNumber: number) => {
    setMediaStatus("idle");
    setMediaError("");
    const response = await api.mediaOverlays(ebookId, pageNumber);
    mediaEditVersion.current += 1;
    setMediaRevision(response.revision);
    setMediaItems(response.items);
    setSelectedMediaId(response.items[0]?.id ?? null);
  }, [ebookId]);

  const loadMore = useCallback(async () => {
    if (
      loadingPagesRef.current
      || (totalRef.current !== null && loadedCountRef.current >= totalRef.current)
    ) return;
    loadingPagesRef.current = true;
    setLoadingPages(true);
    const requestedPage = nextPageRef.current;
    try {
      const response = await api.pages(ebookId, requestedPage, 100);
      setPages((current) => {
        const known = new Set(current.map((page) => page.id));
        const merged = [...current, ...response.items.filter((page) => !known.has(page.id))];
        loadedCountRef.current = merged.length;
        return merged;
      });
      totalRef.current = response.total;
      nextPageRef.current = requestedPage + 1;
      setSelected((current) => current ?? response.items[0]?.pageNumber ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("โหลดรายการหน้าไม่สำเร็จ"));
    } finally {
      loadingPagesRef.current = false;
      setLoadingPages(false);
    }
  }, [__, ebookId]);

  useEffect(() => { void loadMore(); }, [loadMore, reloadVersion]);

  useEffect(() => {
    const element = sentinel.current;
    if (!element) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore();
    }, { rootMargin: "240px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => {
    if (selected === null) return;
    let active = true;
    setPageText(null);
    setSaveStatus("idle");
    void api.pageText(ebookId, selected).then((value) => {
      if (!active) return;
      pageTextRef.current = value;
      draftRef.current = value.effectiveText;
      setPageText(value);
      setDraft(value.effectiveText);
    }).catch((caught) => {
      if (active) setError(caught instanceof Error ? caught.message : __("โหลดข้อความไม่สำเร็จ"));
    });
    return () => { active = false; };
  }, [__, ebookId, reloadVersion, selected]);

  useEffect(() => {
    if (selected === null) {
      setMediaItems([]);
      setSelectedMediaId(null);
      return;
    }
    let active = true;
    setMediaItems([]);
    setSelectedMediaId(null);
    void loadMediaOverlays(selected).catch((caught) => {
      if (!active) return;
      setMediaError(caught instanceof Error ? caught.message : __("โหลด Media overlay ไม่สำเร็จ"));
    });
    return () => { active = false; };
  }, [__, loadMediaOverlays, selected]);

  const reloadContent = useCallback(() => {
    setPages([]);
    setPageText(null);
    setResults([]);
    pageTextRef.current = null;
    loadingPagesRef.current = false;
    nextPageRef.current = 1;
    loadedCountRef.current = 0;
    totalRef.current = null;
    setReloadVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    let active = true;
    void api.contentExtraction(ebookId)
      .then((value) => {
        if (active) setExtraction(value);
      })
      .catch((caught) => {
        if (active) {
          setError(caught instanceof Error
            ? caught.message
            : __("โหลดสถานะการดึงข้อมูลไม่สำเร็จ"));
        }
      });
    return () => { active = false; };
  }, [__, ebookId]);

  useEffect(() => {
    let active = true;
    void api.aiReview(ebookId)
      .then((value) => {
        if (active) setAiReview(value);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [ebookId]);

  useEffect(() => {
    if (!aiReview || !["queued", "processing"].includes(aiReview.status)) return;
    const timerId = window.setInterval(() => {
      if (!navigator.onLine || document.hidden) return;
      void api.aiReview(ebookId)
        .then(setAiReview)
        .catch(() => undefined);
    }, 2_000);
    return () => window.clearInterval(timerId);
  }, [aiReview, ebookId]);

  useEffect(() => {
    if (aiReview?.status !== "ready" || !aiReview.jobId) return;
    if (reloadedAiReviewJobRef.current === aiReview.jobId) return;
    reloadedAiReviewJobRef.current = aiReview.jobId;
    reloadContent();
  }, [aiReview, reloadContent]);

  useEffect(() => {
    if (!extraction || !["queued", "processing"].includes(extraction.status)) return;
    const timerId = window.setInterval(() => {
      if (!navigator.onLine || document.hidden) return;
      void api.contentExtraction(ebookId)
        .then((next) => {
          if (next.status === "ready") reloadContent();
          setExtraction(next);
        })
        .catch(() => undefined);
    }, 2_000);
    return () => window.clearInterval(timerId);
  }, [ebookId, extraction, reloadContent]);

  const persist = useCallback(async (
    text: string,
    reset = false,
  ): Promise<boolean> => {
    const current = pageTextRef.current;
    if (!current || saving.current) return false;
    setSaveStatus("saving");
    const request = api.updatePageText(
      ebookId,
      current.pageNumber,
      reset ? null : text,
      current.revision,
    ).then((updated) => {
      pageTextRef.current = updated;
      setPageText(updated);
      setPages((items) => items.map((page) => page.pageNumber === updated.pageNumber
        ? {
            ...page,
            hasText: Boolean(updated.effectiveText.trim()),
            textEdited: updated.editedText !== null,
            textRevision: updated.revision,
            textTruncated: updated.truncated,
          }
        : page));
      if (draftRef.current === text || reset) {
        draftRef.current = updated.effectiveText;
        setDraft(updated.effectiveText);
        setSaveStatus("saved");
      } else {
        setSaveStatus("pending");
      }
      setError("");
      return true;
    }).catch(async (caught) => {
      setSaveStatus("error");
      if (caught instanceof ApiClientError && caught.code === "PAGE_TEXT_CONFLICT") {
        try {
          const latest = await api.pageText(ebookId, current.pageNumber);
          pageTextRef.current = latest;
          setPageText(latest);
          setError(__("ข้อความถูกแก้ไขจากหน้าต่างอื่น ระบบโหลด revision ล่าสุดแล้วและเก็บข้อความที่คุณพิมพ์ไว้"));
        } catch {
          setError(__("ข้อความถูกแก้ไขจากหน้าต่างอื่น และโหลด revision ล่าสุดไม่สำเร็จ"));
        }
      } else {
        setError(caught instanceof Error ? caught.message : __("บันทึกข้อความไม่สำเร็จ"));
      }
      return false;
    }).finally(() => {
      saving.current = null;
    });
    saving.current = request;
    return request;
  }, [__, ebookId]);

  useEffect(() => {
    if (!pageText || draft === pageText.effectiveText) return;
    setSaveStatus("pending");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void persist(draftRef.current);
    }, 800);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [draft, pageText, persist]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (
        !["pending", "saving", "error"].includes(saveStatus)
        && !["pending", "saving", "error"].includes(mediaStatus)
      ) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [mediaStatus, saveStatus]);

  useEffect(() => {
    if (selected === null || mediaStatus !== "pending") return;
    if (!mediaItemsReadyForSave(mediaItems)) return;
    if (mediaTimer.current) window.clearTimeout(mediaTimer.current);
    mediaTimer.current = window.setTimeout(() => {
      mediaTimer.current = null;
      void saveMediaOverlays(selected, mediaItems, mediaRevision);
    }, 800);
    return () => {
      if (mediaTimer.current) window.clearTimeout(mediaTimer.current);
    };
  }, [mediaItems, mediaRevision, mediaStatus, selected]);

  useEffect(() => {
    const normalized = query.trim();
    if (!normalized) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearching(true);
      void api.searchPageText(ebookId, normalized)
        .then((response) => setResults(response.items))
        .catch((caught) => setError(caught instanceof Error ? caught.message : __("ค้นหาไม่สำเร็จ")))
        .finally(() => setSearching(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [__, ebookId, query, reloadVersion]);

  async function selectPage(pageNumber: number) {
    if (selected === pageNumber) return;
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (pageTextRef.current && draftRef.current !== pageTextRef.current.effectiveText) {
      const saved = await persist(draftRef.current);
      if (!saved) return;
    } else if (saving.current && !(await saving.current)) {
      return;
    }
    if (mediaTimer.current) {
      window.clearTimeout(mediaTimer.current);
      mediaTimer.current = null;
    }
    if (mediaStatus === "pending") {
      const saved = await saveMediaOverlays(selected, mediaItems, mediaRevision);
      if (!saved && mediaItemsReadyForSave(mediaItems)) return;
    } else if (mediaSaving.current && !(await mediaSaving.current)) {
      return;
    }
    setSelected(pageNumber);
  }

  async function resetText() {
    const current = pageTextRef.current;
    if (!current) return;
    draftRef.current = current.extractedText;
    setDraft(current.extractedText);
    await persist(current.extractedText, true);
  }

  async function confirmExtraction() {
    setQueueingExtraction(true);
    setError("");
    try {
      const next = await api.extractContent(ebookId);
      setExtraction(next);
      setExtractDialogOpen(false);
    } catch (caught) {
      setError(caught instanceof Error
        ? caught.message
        : __("เริ่มดึงข้อมูลจาก PDF ไม่สำเร็จ"));
    } finally {
      setQueueingExtraction(false);
    }
  }

  function parseReviewPageNumbers() {
    return [...new Set(reviewPageNumbers
      .split(",")
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value) => Number.isInteger(value) && value >= 1 && value <= maxEbookPages))]
      .sort((left, right) => left - right);
  }

  async function startReview() {
    const pageNumbers = reviewScope === "selected_pages" ? parseReviewPageNumbers() : undefined;
    if (reviewScope === "selected_pages" && (!pageNumbers || pageNumbers.length === 0)) return;
    setQueueingReview(true);
    setError("");
    try {
      const next = await api.startAiReview(
        ebookId,
        reviewScope,
        undefined,
        pageNumbers,
      );
      setAiReview(next);
      setReviewDialogOpen(false);
    } catch (caught) {
      setError(caught instanceof Error
        ? caught.message
        : __("เริ่มตรวจทานด้วย AI ไม่สำเร็จ"));
    } finally {
      setQueueingReview(false);
    }
  }

  function updateMediaItem(id: string, update: Partial<MediaOverlay>) {
    mediaEditVersion.current += 1;
    setMediaItems((items) => items.map((item) => item.id === id ? { ...item, ...update } : item));
    setMediaStatus("pending");
  }

  function updateMediaRect(id: string, rect: MediaOverlay["rect"]) {
    mediaEditVersion.current += 1;
    setMediaItems((items) => items.map((item) => item.id === id ? { ...item, rect } : item));
    setMediaStatus("pending");
  }

  function addUrlOverlay(kind: "youtube" | "audio_url") {
    if (selected === null) return;
    const item = defaultMediaOverlay(kind, selected, (totalRef.current ?? pages.length) || maxEbookPages);
    mediaEditVersion.current += 1;
    setMediaItems((items) => [...items, item]);
    setSelectedMediaId(item.id);
    setMediaStatus("pending");
    setEffectsDialogOpen(true);
  }

  function addLinkOverlay() {
    if (selected === null) return;
    const item = defaultMediaOverlay("page_link", selected, (totalRef.current ?? pages.length) || maxEbookPages);
    mediaEditVersion.current += 1;
    setMediaItems((items) => [...items, item]);
    setSelectedMediaId(item.id);
    setMediaStatus("pending");
    setEffectsDialogOpen(true);
  }

  function removeMediaOverlay(id: string) {
    mediaEditVersion.current += 1;
    setMediaItems((items) => items.filter((item) => item.id !== id));
    setSelectedMediaId((current) => current === id ? null : current);
    setMediaStatus("pending");
  }

  function changeMediaSource(item: MediaOverlay, nextKind: MediaOverlayKind) {
    const isAudio = nextKind.startsWith("audio");
    updateMediaItem(item.id, {
      kind: nextKind,
      sourceUrl: nextKind === "youtube"
        ? "https://www.youtube.com/watch?v="
        : nextKind.endsWith("_url")
          ? ""
          : null,
      assetKey: null,
      assetUrl: null,
      contentType: null,
      sizeBytes: null,
      targetPageNumber: null,
      rect: {
        ...item.rect,
        width: isAudio ? Math.min(item.rect.width, 45) : Math.max(item.rect.width, 50),
        height: isAudio ? 10 : Math.max(item.rect.height, 30),
      },
    });
  }

  function mediaInput(item: MediaOverlay): MediaOverlayInput {
    return {
      ...(item.id.startsWith("draft-") ? {} : { id: item.id }),
      kind: item.kind,
      sourceUrl: item.sourceUrl,
      assetKey: item.assetKey,
      targetPageNumber: item.targetPageNumber,
      rect: item.rect,
      zIndex: item.zIndex,
      animationType: item.animationType,
      animationDelayMs: item.animationDelayMs,
      animationDurationMs: item.animationDurationMs,
      enabled: item.enabled,
    };
  }

  function mediaItemsReadyForSave(items: MediaOverlay[]): boolean {
    return items.every((item) => MediaOverlayInputSchema.safeParse(mediaInput(item)).success);
  }

  async function saveMediaOverlays(pageNumber = selected, items = mediaItems, revision = mediaRevision) {
    if (pageNumber === null || mediaSaving.current) return false;
    if (!mediaItemsReadyForSave(items)) return false;
    const savedEditVersion = mediaEditVersion.current;
    setMediaStatus("saving");
    setMediaError("");
    const request = api.updateMediaOverlays(
      ebookId,
      pageNumber,
      revision,
      items.map(mediaInput),
    ).then((response) => {
      setMediaRevision(response.revision);
      if (mediaEditVersion.current === savedEditVersion) {
        mediaLastSavedVersion.current = savedEditVersion;
        setMediaItems(response.items);
        setSelectedMediaId((current) => response.items.find((item) => item.id === current)?.id ?? response.items[0]?.id ?? null);
        setMediaStatus("saved");
      } else {
        setMediaStatus("pending");
      }
      return true;
    }).catch(async (caught) => {
      setMediaStatus("error");
      if (caught instanceof ApiClientError && caught.code === "MEDIA_OVERLAY_CONFLICT") {
        setMediaError(__("Media overlay ถูกแก้ไขจากหน้าต่างอื่น ระบบโหลด revision ล่าสุดแล้ว"));
        await loadMediaOverlays(pageNumber).catch(() => undefined);
      } else {
        setMediaError(caught instanceof Error ? caught.message : __("บันทึก Media overlay ไม่สำเร็จ"));
      }
      return false;
    }).finally(() => {
      mediaSaving.current = null;
      if (mediaEditVersion.current > mediaLastSavedVersion.current) {
        setMediaStatus("pending");
      }
    });
    mediaSaving.current = request;
    return request;
  }

  async function uploadMediaOverlay(kind: "audio_asset" | "video_asset", file: File, replaceId?: string) {
    if (selected === null) return;
    setMediaUploading(kind);
    setMediaError("");
    try {
      const upload = await api.startMediaOverlayUpload(ebookId, selected, {
        kind,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });
      await uploadObjectAsset(file, upload);
      const response = await api.completeMediaOverlayUpload(ebookId, selected, kind, upload.objectKey);
      mediaEditVersion.current += 1;
      const nextItems = replaceId
        ? response.items.filter((item) => item.id !== replaceId)
        : response.items;
      setMediaRevision(response.revision);
      setMediaItems(nextItems);
      setSelectedMediaId(nextItems.at(-1)?.id ?? nextItems[0]?.id ?? null);
      setMediaStatus(replaceId && response.items.some((item) => item.id === replaceId) ? "pending" : "saved");
    } catch (caught) {
      setMediaError(caught instanceof Error ? caught.message : __("อัปโหลด Media ไม่สำเร็จ"));
      setMediaStatus("error");
    } finally {
      setMediaUploading(null);
    }
  }

  const extractionActive = Boolean(
    extraction && ["queued", "processing"].includes(extraction.status),
  );
  const reviewActive = Boolean(
    aiReview && ["queued", "processing"].includes(aiReview.status),
  );
  const selectedReview = aiReview?.pages.find(
    (page) => page.pageNumber === selected && page.status === "ready",
  );
  const visibleItems = query.trim() ? results : pages;
  const selectedPage = pages.find((page) => page.pageNumber === selected);
  const selectedSearchResult = results.find((page) => page.pageNumber === selected);
  const selectedPreviewUrl = selectedPage?.imageUrl ?? selectedSearchResult?.thumbnailUrl ?? null;
  const selectedMediaItem = mediaItems.find((item) => item.id === selectedMediaId) ?? null;
  return <Stack spacing={2}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      <GlobalInput
        value={query}
        onChange={(event) => setQuery(event.target.value.slice(0, 100))}
        placeholder={__("ค้นหาข้อความทุกหน้า")}
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "divider",
            borderWidth: 1,
          },
          "& .MuiOutlinedInput-root.Mui-focused": {
            boxShadow: "none",
            outline: "none",
          },
          "& input:focus": {
            outline: "none",
          },
        }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          endAdornment: searching ? <CircularProgress size={18} /> : undefined,
        }}
      />
      <Button
        variant="outlined"
        startIcon={extractionActive ? <CircularProgress size={18} /> : <Refresh />}
        disabled={extractionActive}
        onClick={() => setExtractDialogOpen(true)}
        sx={{ flexShrink: 0 }}
      >
        {extractionActive ? __("กำลังดึงข้อมูล…") : __("ดึงข้อมูลจากไฟล์ PDF")}
      </Button>
      <Button
        variant="contained"
        startIcon={reviewActive ? <CircularProgress size={18} /> : <AutoFixHigh />}
        disabled={extractionActive || reviewActive || selected === null}
        onClick={() => {
          setReviewScope("selected_pages");
          setReviewPageNumbers(selected === null ? "" : String(selected));
          setReviewDialogOpen(true);
        }}
        sx={{ flexShrink: 0 }}
      >
        {reviewActive ? __("AI กำลังตรวจทาน…") : __("ตรวจทานด้วย AI")}
      </Button>
    </Stack>
    {extraction?.status === "failed" && (
      <Alert
        severity="error"
        action={<Button color="inherit" onClick={() => setExtractDialogOpen(true)}>{__("ลองอีกครั้ง")}</Button>}
      >
        {__("ดึงข้อมูลจาก PDF ไม่สำเร็จ: {{reason}}", { reason: extraction.error ?? __("ไม่ทราบสาเหตุ") })}
      </Alert>
    )}
    {extraction?.status === "queued" && (
      <Alert severity="info">
        {__("กำลังรอระบบประมวลผลรับงาน หากสถานะนี้ไม่เปลี่ยนภายในประมาณ 30 วินาที ให้ตรวจสอบว่า converter service กำลังทำงานอยู่")}
      </Alert>
    )}
    {extraction?.status === "processing" && (
      <Alert severity="info" icon={false}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2">
              {extractionStageLabel(extraction, __)}
            </Typography>
            {extraction.stage !== "downloading"
              && extraction.stage !== "extracting_text" && (
              <Typography variant="body2" fontWeight={600}>
                {extraction.progress}%
              </Typography>
            )}
          </Stack>
          <LinearProgress
            aria-label={__("ความคืบหน้าการดึงข้อมูลจาก PDF")}
            variant={
              extraction.stage === "downloading"
                || extraction.stage === "extracting_text"
                ? "indeterminate"
                : "determinate"
            }
            value={extraction.progress}
            sx={{ height: 8, borderRadius: 8 }}
          />
          {extraction.stage === "ocr" && (
            <Typography variant="caption" color="text.secondary">
              {__("ตรวจคุณภาพทุกหน้า และใช้ OCR เฉพาะหน้าที่ Text Layer ว่างหรือคะแนนต่ำ")}
            </Typography>
          )}
        </Stack>
      </Alert>
    )}
    {aiReview && ["queued", "processing"].includes(aiReview.status) && (
      <Alert severity="info" icon={<AutoFixHigh />}>
        <Stack spacing={1} width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">
              {aiReview.status === "queued"
                ? __("กำลังรอ AI reviewer รับงาน")
                : __("AI กำลังตรวจทานและบันทึกอัตโนมัติด้วย {{model}}", { model: aiReview.model ?? "" })}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {aiReview.processedPages}/{aiReview.totalPages} {__("หน้า")} ({aiReview.totalPages > 0
                ? Math.round((aiReview.processedPages / aiReview.totalPages) * 100)
                : 0}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant={aiReview.status === "queued" ? "indeterminate" : "determinate"}
            value={aiReview.totalPages > 0
              ? (aiReview.processedPages / aiReview.totalPages) * 100
              : 0}
            sx={{ height: 8, borderRadius: 8 }}
          />
          {aiReview.totalPages > 1 && (
            <Typography variant="caption" color="text.secondary">
              {__("ระบบกำลังตรวจหลายหน้าและบันทึกผลที่ผ่านเงื่อนไขให้อัตโนมัติ")}
            </Typography>
          )}
        </Stack>
      </Alert>
    )}
    {aiReview?.status === "failed" && (
      <Alert severity="error">
        {__("ตรวจทานด้วย AI ไม่สำเร็จ: {{reason}}", { reason: aiReview.error ?? __("ไม่ทราบสาเหตุ") })}
      </Alert>
    )}
    {query.trim() && (
      <Typography variant="body2" color="text.secondary">
        {__("พบ {{count}} หน้า", { count: results.length })}
      </Typography>
    )}
    {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      minHeight: 560,
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      overflow: "hidden",
      }}>
      <Box sx={{
        width: { xs: "100%", md: 232 },
        maxHeight: { xs: query.trim() ? "none" : 152, md: 680 },
        overflow: "auto",
        p: 1,
        display: "flex",
        flexDirection: { xs: query.trim() ? "column" : "row", md: "column" },
        gap: 0.75,
      }}>
        {visibleItems.map((item) => {
          const pageNumber = item.pageNumber;
          const thumbnailUrl = item.thumbnailUrl;
          const active = selected === pageNumber;
          return <Button
            key={pageNumber}
            onClick={() => void selectPage(pageNumber)}
            variant={active ? "contained" : "text"}
            aria-current={active ? "page" : undefined}
            sx={{
              p: 0.75,
              minWidth: { xs: 88, md: "100%" },
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              border: 2,
              borderColor: active ? "primary.main" : "transparent",
              boxShadow: active ? (theme) => `0 0 0 2px ${theme.palette.primary.light}` : "none",
              "&:hover": {
                borderColor: active ? "primary.main" : "divider",
              },
            }}
          >
            <Stack direction="row" gap={1} width="100%" alignItems="center">
              <Box
                component="img"
                src={thumbnailUrl}
                alt={__("หน้า {{page}}", { page: pageNumber })}
                loading="lazy"
                sx={{
                  width: { xs: 48, md: 48 },
                  height: { xs: 64, md: 62 },
                  flexShrink: 0,
                  objectFit: "contain",
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              />
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={700}>{__("หน้า {{page}}", { page: pageNumber })}</Typography>
                {"snippet" in item && (
                  <Typography variant="caption" sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    {item.snippet}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Button>;
        })}
        {!query.trim() && <Box ref={sentinel} sx={{ minWidth: 1, minHeight: 1 }} />}
        {loadingPages && <CircularProgress size={22} sx={{ m: 2, alignSelf: "center" }} />}
        {!loadingPages && visibleItems.length === 0 && (
          <Typography variant="body2" color="text.secondary" p={2}>{__("ไม่พบหน้า")}</Typography>
        )}
      </Box>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
      <Divider sx={{ display: { xs: "block", md: "none" } }} />
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        {!pageText ? (
          selected === null
            ? <EmptyState title={__("เลือกหน้า")} description={__("เลือกภาพหน้าจากรายการเพื่อดูและแก้ไขข้อความ")} />
            : <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}><CircularProgress /></Box>
        ) : <Stack spacing={2} height="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={800}>{__("หน้า {{page}}", { page: pageText.pageNumber })}</Typography>
                <Chip
                  size="small"
                  color={pageText.editedText !== null
                    ? "primary"
                    : pageText.textSource === "ocr"
                      ? "warning"
                      : "default"}
                  label={pageText.editedText !== null
                    ? __("แก้ไขเอง")
                    : pageText.textSource === "ocr"
                      ? `OCR${pageText.ocrConfidence === null
                        ? ""
                        : ` ${Math.round(pageText.ocrConfidence)}%`}`
                      : __("PDF text")}
                />
                {pageText.textQuality !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {__("คุณภาพ {{score}}/100", { score: pageText.textQuality })}
                  </Typography>
                )}
                {pageText.ocrAttempted && pageText.textSource === "pdf" && (
                  <Chip size="small" variant="outlined" label={__("ตรวจ OCR แล้ว")} />
                )}
                {pageText.textSource === "ocr"
                  && pageText.normalizationSummary?.changed && (
                  <Chip size="small" color="success" label={__("จัดสระไทยแล้ว")} />
                )}
                {pageText.textSource === "ocr"
                  && pageText.spellSuggestions.length > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={__("ควรตรวจ {{count}} คำ", { count: pageText.spellSuggestions.length })}
                  />
                )}
                {selectedReview && (
                  <Tooltip title={__("ดูรายละเอียดคำแนะนำจาก AI")}>
                    <IconButton
                      size="small"
                      color={selectedReview.appliedAt ? "success" : "warning"}
                      onClick={() => setReviewDetailsOpen(true)}
                      aria-label={__("ดูรายละเอียดคำแนะนำจาก AI")}
                    >
                      <AutoFixHigh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" color={
                saveStatus === "error" ? "error" : "text.secondary"
              }>
                {{
                  idle: pageText.editedText === null ? __("ข้อความจาก PDF") : __("แก้ไขแล้ว"),
                  pending: __("รอบันทึก…"),
                  saving: __("กำลังบันทึก…"),
                  saved: __("บันทึกแล้ว"),
                  error: __("บันทึกไม่สำเร็จ"),
                }[saveStatus]}
              </Typography>
            </Box>
            <Button
              startIcon={<Restore />}
              disabled={
                extractionActive
                || pageText.editedText === null && draft === pageText.extractedText
              }
              onClick={() => void resetText()}
            >
              {__("คืนค่าจาก PDF")}
            </Button>
          </Box>
          {pageText.truncated && (
            <Alert severity="warning">{__("ข้อความจาก PDF ยาวเกินขีดจำกัดและถูกตัดบางส่วน")}</Alert>
          )}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 420px" },
            gap: 2,
            alignItems: "start",
            minWidth: 0,
          }}>
            <GlobalInput
              multiline
              fullWidth
              minRows={18}
              maxRows={28}
              value={draft}
              disabled={extractionActive}
              onChange={(event) => {
                const value = event.target.value.slice(0, 1_000_000);
                draftRef.current = value;
                setDraft(value);
              }}
              placeholder={__("ไม่พบข้อความในหน้านี้ คุณสามารถเพิ่มข้อความสำหรับการค้นหาได้")}
              inputProps={{ "aria-label": __("ข้อความสำหรับค้นหาของหน้า {{page}}", { page: pageText.pageNumber }) }}
            />
            <Box
              component="aside"
              sx={{
                position: { lg: "sticky" },
                top: { lg: 16 },
                minWidth: 0,
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                {__("ตัวอย่างหน้า {{page}}", { page: pageText.pageNumber })}
              </Typography>
              {selectedPreviewUrl ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    overflow: "hidden",
                    lineHeight: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={selectedPreviewUrl}
                    alt={__("ตัวอย่างหน้า {{page}}", { page: pageText.pageNumber })}
                    sx={{
                      display: "block",
                      width: "100%",
                      maxHeight: { xs: 440, lg: 560 },
                      objectFit: "contain",
                    }}
                  />
                  {mediaItems.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => setSelectedMediaId(item.id)}
                      sx={{
                        position: "absolute",
                        left: `${item.rect.left}%`,
                        top: `${item.rect.top}%`,
                        width: `${item.rect.width}%`,
                        height: `${item.rect.height}%`,
                        zIndex: item.zIndex,
                        minHeight: 18,
                        border: 2,
                        borderColor: selectedMediaId === item.id ? "primary.main" : "warning.main",
                        bgcolor: item.kind === "page_link"
                          ? "rgba(46,125,50,0.16)"
                          : item.kind.includes("audio")
                            ? "rgba(25,118,210,0.18)"
                            : "rgba(255,152,0,0.18)",
                        cursor: "pointer",
                        lineHeight: 1.2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: 4,
                          top: 3,
                          px: 0.5,
                          borderRadius: 0.75,
                          bgcolor: "rgba(255,255,255,0.86)",
                          color: "text.primary",
                          fontWeight: 700,
                          pointerEvents: "none",
                        }}
                      >
                        {__(mediaKindLabels[item.kind])}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {__("ไม่พบภาพตัวอย่างของหน้านี้")}
                </Typography>
              )}
              <Stack spacing={1} mt={1.5}>
                <Typography variant="caption" color={mediaStatus === "error" ? "error" : "text.secondary"}>
                  {{
                    idle: mediaItems.length > 0
                      ? __("มีเอฟเฟค {{count}} รายการ", { count: mediaItems.length })
                      : __("ยังไม่มีเอฟเฟคในหน้านี้"),
                    pending: mediaItemsReadyForSave(mediaItems)
                      ? __("รอบันทึกเอฟเฟคอัตโนมัติ…")
                      : __("กรอกข้อมูลเอฟเฟคให้ครบก่อนบันทึกอัตโนมัติ"),
                    saving: __("กำลังบันทึกเอฟเฟคอัตโนมัติ…"),
                    saved: __("บันทึกเอฟเฟคอัตโนมัติแล้ว"),
                    error: __("บันทึกเอฟเฟคอัตโนมัติไม่สำเร็จ"),
                  }[mediaStatus]}
                </Typography>
                {mediaError && <Alert severity="error">{mediaError}</Alert>}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={selected === null}
                  onClick={() => setEffectsDialogOpen(true)}
                >
                  {__("เพิ่มเอฟเฟคบนหน้านี้")}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Stack>}
      </Box>
    </Box>
    <PageEffectsDialog
      open={effectsDialogOpen}
      onClose={() => setEffectsDialogOpen(false)}
      pageNumber={selected}
      pageCount={(totalRef.current ?? pages.length) || maxEbookPages}
      previewUrl={selectedPreviewUrl}
      items={mediaItems}
      selectedItem={selectedMediaItem}
      selectedItemId={selectedMediaId}
      saveStatus={mediaStatus}
      error={mediaError}
      uploading={mediaUploading}
      revision={mediaRevision}
      onSelectItem={setSelectedMediaId}
      onAddVideo={() => addUrlOverlay("youtube")}
      onAddAudio={() => addUrlOverlay("audio_url")}
      onAddLink={addLinkOverlay}
      onUpdateItem={updateMediaItem}
      onUpdateRect={updateMediaRect}
      onChangeSource={changeMediaSource}
      onDelete={removeMediaOverlay}
      onUpload={(kind, file, replaceId) => void uploadMediaOverlay(kind, file, replaceId)}
      itemsReadyForSave={mediaItemsReadyForSave(mediaItems)}
    />
    <Dialog
      open={reviewDialogOpen}
      onClose={() => {
        if (!queueingReview) setReviewDialogOpen(false);
      }}
      aria-labelledby="ai-review-dialog-title"
    >
      <DialogTitle id="ai-review-dialog-title">{__("ตรวจทานด้วย AI")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1, minWidth: { xs: 260, sm: 420 } }}>
          <DialogContentText>
            {__("ระบบจะส่งข้อความตามช่วงหน้าที่เลือกไปยัง OpenAI เพื่อตรวจทานและบันทึกผลอัตโนมัติ โดยไม่แต่งเนื้อหาใหม่")}
          </DialogContentText>
          <GlobalInput
            select
            fullWidth
            label={__("ช่วงหน้าที่ต้องการตรวจ")}
            value={reviewScope}
            onChange={(event) => setReviewScope(event.target.value as AiReviewScope)}
          >
            <MenuItem value="selected_pages">{__("เฉพาะหน้า")}</MenuItem>
            <MenuItem value="low_quality_pages">{__("หน้าคะแนนต่ำ")}</MenuItem>
            <MenuItem value="not_ai_reviewed_pages">{__("ทุกหน้าที่ยังไม่ได้รับการแก้ไขด้วย AI")}</MenuItem>
            <MenuItem value="unedited_pages">{__("ทุกหน้าที่ยังไม่มีการแก้ไข")}</MenuItem>
            <MenuItem value="all_pages">{__("ทุกหน้า")}</MenuItem>
          </GlobalInput>
          {reviewScope === "selected_pages" && (
            <GlobalInput
              fullWidth
              label={__("เลขหน้า")}
              value={reviewPageNumbers}
              onChange={(event) => setReviewPageNumbers(event.target.value)}
              helperText={__("คั่นหลายหน้าด้วย comma เช่น 1, 3, 8")}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap" }}>
        <Button
          disabled={queueingReview}
          onClick={() => setReviewDialogOpen(false)}
        >
          {__("ยกเลิก")}
        </Button>
        <Button
          variant="contained"
          disabled={queueingReview || (reviewScope === "selected_pages" && parseReviewPageNumbers().length === 0)}
          onClick={() => void startReview()}
        >
          {queueingReview ? __("กำลังเริ่ม…") : __("เริ่มตรวจและบันทึกอัตโนมัติ")}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={reviewDetailsOpen && Boolean(selectedReview)}
      onClose={() => setReviewDetailsOpen(false)}
      maxWidth="md"
      fullWidth
      aria-labelledby="ai-review-details-title"
    >
      <DialogTitle id="ai-review-details-title">{__("รายละเอียดคำแนะนำจาก AI")}</DialogTitle>
      <DialogContent>
        {selectedReview && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity={selectedReview.appliedAt ? "success" : "warning"}>
              {selectedReview.appliedAt
                ? __("ผลตรวจทานถูกบันทึกอัตโนมัติแล้ว")
                : __("ผลตรวจทานยังไม่ถูกบันทึก เพราะข้อความหน้านี้เปลี่ยนระหว่างตรวจ")}
            </Alert>
            <Box>
              <Typography fontWeight={700}>{__("สรุป")}</Typography>
              <Typography variant="body2">
                {selectedReview.summary || __("AI ตรวจทานข้อความหน้านี้แล้ว")}
              </Typography>
            </Box>
            {selectedReview.changes.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {selectedReview.changes.map((change, index) => (
                  <Typography component="li" variant="body2" key={`${change.before}-${index}`}>
                    “{change.before}” → “{change.after}” — {change.reason}
                  </Typography>
                ))}
              </Box>
            )}
            <GlobalInput
              label={__("ข้อความที่ AI ตรวจทานแล้ว")}
              value={selectedReview.correctedText ?? ""}
              multiline
              minRows={10}
              maxRows={18}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReviewDetailsOpen(false)}>{__("ปิด")}</Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={extractDialogOpen}
      onClose={() => {
        if (!queueingExtraction) setExtractDialogOpen(false);
      }}
      aria-labelledby="confirm-content-extraction-title"
    >
      <DialogTitle id="confirm-content-extraction-title">{__("ยืนยัน")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {__("ระบบจะดึงข้อความและสารบัญจากไฟล์ PDF ต้นฉบับอีกครั้ง ข้อมูลข้อความเดิมที่เคยดึงมา รวมถึงข้อความที่แก้ไขเองทุกหน้า จะถูกทับทั้งหมดและไม่สามารถเรียกคืนได้")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={queueingExtraction}
          onClick={() => setExtractDialogOpen(false)}
        >
          {__("ยกเลิก")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={queueingExtraction}
          onClick={() => void confirmExtraction()}
        >
          {queueingExtraction ? __("กำลังเริ่ม…") : __("ยืนยันและดึงข้อมูล")}
        </Button>
      </DialogActions>
    </Dialog>
  </Stack>;
}
